import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles,
  TrendingUp,
  TrendingDown,
  Banknote,
  Droplets,
  CalendarClock,
  Trophy,
  Percent,
} from "lucide-react";
import DashboardLayout from "../components/layout/DashboardLayout";
import { api, fmtINR } from "../lib/api";
import { assetTotal, assetInvested } from "../lib/assets";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  AreaChart,
  Area,
} from "recharts";

// ---------------------------------------------------------------------------
// helpers — valuation rules live in ../lib/assets
// ---------------------------------------------------------------------------
const num = (v) => Number(v || 0);
const titleize = (s) =>
  (s || "")
    .toString()
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());

const fmtDate = (d) =>
  d
    ? new Date(d).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : null;

const yearsBetween = (a, b) =>
  (new Date(b).getTime() - new Date(a).getTime()) / (365.25 * 24 * 3600 * 1000);

// liquidity buckets
const IMMEDIATE = new Set(["cash", "bank"]);
const SHORT = new Set([
  "stocks",
  "mutual_funds",
  "gold",
  "silver",
  "crypto",
  "digital",
  "bonds",
]);
const bucketOf = (a) => {
  if (a.is_liquid || IMMEDIATE.has(a.category)) return "immediate";
  if (SHORT.has(a.category)) return "short";
  return "locked";
};

// monthly amortization of a single loan
function amortize(l) {
  let bal = num(l.outstanding);
  const emi = num(l.emi);
  const r = num(l.interest_rate) / 100 / 12;
  if (bal <= 0) return { months: 0, interest: 0, balances: [0] };
  if (emi <= 0) return { months: null, interest: null, balances: null };
  let interest = 0;
  let months = 0;
  const balances = [bal];
  while (bal > 0 && months < 1200) {
    const i = bal * r;
    const principal = emi - i;
    if (principal <= 0) return { months: null, interest: null, balances: null }; // never amortizes
    bal = Math.max(0, bal - principal);
    interest += i;
    months += 1;
    balances.push(bal);
  }
  return { months, interest, balances };
}
const balanceAt = (am, month) => {
  if (!am.balances) return null;
  if (month >= am.balances.length) return 0;
  return am.balances[month];
};

export default function Insights() {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      const safe = (p) => p.then((r) => r.data).catch(() => ({}));
      const [a, l, g] = await Promise.all([
        safe(api.get("/assets")),
        safe(api.get("/liabilities")),
        safe(api.get("/goals")),
      ]);
      setData({
        assets: a.assets || [],
        liabilities: l.liabilities || [],
        goals: g.goals || [],
      });
      setLoading(false);
    })();
  }, []);

  return (
    <DashboardLayout>
      <div className="mb-8 flex items-center gap-3">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-gradient-to-br from-emerald-500 to-blue-600 text-white">
          <Sparkles className="h-5 w-5" />
        </div>
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">
            Reports & analysis
          </p>
          <h1
            data-testid="insights-title"
            className="font-display text-3xl sm:text-4xl font-bold tracking-tighter text-slate-900 dark:text-white"
          >
            Insights
          </h1>
        </div>
      </div>
      {loading ? <Skeleton /> : <Body data={data} />}
    </DashboardLayout>
  );
}

function Body({ data }) {
  const { assets, liabilities, goals } = data;

  if (assets.length === 0 && liabilities.length === 0) {
    return (
      <div className="card-soft p-12 text-center text-slate-500">
        Add assets and liabilities to unlock performance, cashflow and debt
        reports.
      </div>
    );
  }

  // ---- performance ----
  // Only GROWTH assets belong here (capital gain = current vs invested).
  // Income assets like MIS earn via monthly payouts, not appreciation, so they
  // live in the cashflow card — including them here would just show a noisy 0%.
  const withCost = assets
    .filter(
      (a) => assetInvested(a) > 0 && !a.synthetic && a.category !== "mis",
    )
    .map((a) => {
      const invested = assetInvested(a);
      const cur = assetTotal(a);
      const yrs = a.purchase_date ? yearsBetween(a.purchase_date, new Date()) : null;
      const retPct = invested > 0 ? (cur / invested - 1) * 100 : 0;
      const cagr =
        yrs && yrs >= 0.1 && invested > 0
          ? (Math.pow(cur / invested, 1 / yrs) - 1) * 100
          : null;
      return { id: a.id, name: a.name, invested, cur, gain: cur - invested, retPct, cagr };
    })
    .sort((a, b) => b.retPct - a.retPct);

  const totInvested = withCost.reduce((s, x) => s + x.invested, 0);
  const totCurrent = withCost.reduce((s, x) => s + x.cur, 0);
  const totGain = totCurrent - totInvested;
  const totRet = totInvested > 0 ? (totGain / totInvested) * 100 : 0;
  const best = withCost[0];

  // ---- monthly cashflow: money IN (green) vs money OUT (red) ----
  // IN  = passive income from Monthly Income Scheme (MIS) assets.
  // OUT = everything leaving your bank each month: EMIs (debt) + SIPs
  //       (investing is still money you pay out).
  const inflowRows = assets
    .filter((a) => a.category === "mis" && !a.synthetic)
    .map((a) => ({
      name: a.name,
      amt: (num(a.value) * num(a.expected_return)) / 1200,
      kind: "MIS",
    }))
    .filter((r) => r.amt > 0)
    .sort((a, b) => b.amt - a.amt);

  const emiRows = liabilities
    .filter((l) => l.is_active !== false && num(l.emi) > 0 && !l.synthetic)
    .map((l) => ({ name: l.name, amt: num(l.emi), kind: "EMI" }));
  const sipRows = assets
    .filter((a) => num(a.monthly_contribution) > 0 && !a.synthetic)
    .map((a) => ({ name: a.name, amt: num(a.monthly_contribution), kind: "SIP" }));
  const outflowRows = [...emiRows, ...sipRows].sort((a, b) => b.amt - a.amt);

  const monthlyIn = inflowRows.reduce((s, x) => s + x.amt, 0);
  const monthlyOut = outflowRows.reduce((s, x) => s + x.amt, 0);
  const netCashflow = monthlyIn - monthlyOut;
  // kept for the header/other cards that reference these
  const monthlyEMI = emiRows.reduce((s, x) => s + x.amt, 0);
  const monthlySIP = sipRows.reduce((s, x) => s + x.amt, 0);

  // ---- debt ----
  const loans = liabilities
    .filter((l) => num(l.outstanding) > 0 && !l.synthetic && l.is_active !== false)
    .map((l) => ({ ...l, am: amortize(l) }));
  const totOutstanding = loans.reduce((s, l) => s + num(l.outstanding), 0);
  const totInterest = loans.reduce((s, l) => s + (l.am.interest || 0), 0);
  const anyInterest = loans.some((l) => l.am.interest != null);
  const wRate =
    totOutstanding > 0
      ? loans.reduce((s, l) => s + num(l.interest_rate) * num(l.outstanding), 0) /
        totOutstanding
      : 0;
  const maxMonths = Math.min(
    Math.max(0, ...loans.map((l) => l.am.months || 0)),
    360,
  );
  const debtYears = Math.ceil(maxMonths / 12);
  const debtSeries = [];
  for (let y = 0; y <= debtYears; y++) {
    const m = y * 12;
    let total = 0;
    let known = true;
    for (const l of loans) {
      const b = balanceAt(l.am, m);
      if (b == null) known = false;
      else total += b;
    }
    debtSeries.push({ year: new Date().getFullYear() + y, balance: Math.round(total), known });
  }
  const debtFreeYear =
    maxMonths > 0 ? new Date().getFullYear() + Math.ceil(maxMonths / 12) : null;

  // ---- liquidity ladder ----
  const ladder = { immediate: 0, short: 0, locked: 0 };
  for (const a of assets) ladder[bucketOf(a)] += assetTotal(a);
  const ladderTotal = ladder.immediate + ladder.short + ladder.locked;

  // ---- maturity calendar ----
  const now = new Date();
  const events = [];
  for (const a of assets) {
    if (a.maturity_date && new Date(a.maturity_date) > now)
      events.push({
        date: a.maturity_date,
        title: a.name,
        kind: "Asset matures",
        amount: assetTotal(a),
        tone: "emerald",
      });
  }
  for (const l of liabilities) {
    if (l.end_date && new Date(l.end_date) > now)
      events.push({
        date: l.end_date,
        title: l.name,
        kind: "Loan ends",
        amount: num(l.outstanding),
        tone: "blue",
      });
  }
  for (const g of goals) {
    if (g.target_date && new Date(g.target_date) > now)
      events.push({
        date: g.target_date,
        title: g.name,
        kind: "Goal target",
        amount: num(g.target_amount),
        tone: "violet",
      });
  }
  events.sort((a, b) => new Date(a.date) - new Date(b.date));

  return (
    <div className="space-y-6">
      {/* KPI strip — figures the dashboard doesn't surface */}
      <section className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <Kpi
          icon={Trophy}
          label="Unrealized gain"
          value={fmtINR(totGain, { compact: true })}
          sub={totInvested > 0 ? `${totRet >= 0 ? "+" : ""}${totRet.toFixed(1)}% overall` : "Add cost data"}
          accent={totGain >= 0 ? "text-emerald-600" : "text-rose-600"}
        />
        <Kpi
          icon={Banknote}
          label="Investing / month"
          value={fmtINR(monthlySIP, { compact: true })}
          sub={`${fmtINR(monthlySIP * 12, { compact: true })} / yr`}
        />
        <Kpi
          icon={TrendingDown}
          label="EMIs / month"
          value={fmtINR(monthlyEMI, { compact: true })}
          sub={debtFreeYear ? `debt-free ~${debtFreeYear}` : "—"}
          accent="text-rose-600"
        />
        <Kpi
          icon={Percent}
          label="Interest you'll pay"
          value={anyInterest ? fmtINR(totInterest, { compact: true }) : "—"}
          sub={totOutstanding > 0 ? `avg ${wRate.toFixed(1)}% p.a.` : "No debt"}
          accent="text-amber-600"
        />
      </section>

      {/* Portfolio performance */}
      <Card
        icon={TrendingUp}
        title="Portfolio performance"
        desc="Returns since purchase — which holdings are actually working for you."
      >
        {withCost.length === 0 ? (
          <Empty>
            Add a purchase value &amp; date to your assets to see returns and
            CAGR.
          </Empty>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Invested" value={fmtINR(totInvested, { compact: true })} />
              <Stat
                label="Current"
                value={fmtINR(totCurrent, { compact: true })}
                accent="text-emerald-600"
              />
              <Stat
                label="Gain"
                value={`${totGain >= 0 ? "+" : ""}${fmtINR(totGain, { compact: true })}`}
                accent={totGain >= 0 ? "text-emerald-600" : "text-rose-600"}
              />
              <Stat label="Return" value={`${totRet.toFixed(1)}%`} />
            </div>

            {/* Ranked holdings — every % is backed by its actual ₹ values,
                so 0% (current = invested) and big gains are self-explanatory. */}
            <div className="mt-5 space-y-2">
              {withCost.map((h) => {
                const up = h.retPct >= 0;
                return (
                  <div
                    key={h.id}
                    className="flex items-center gap-3 rounded-xl border border-slate-200 dark:border-slate-800 px-3.5 py-2.5"
                  >
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-slate-900 dark:text-white truncate">
                        {h.name}
                      </div>
                      <div className="text-xs text-slate-400 num-mono">
                        {fmtINR(h.invested, { compact: true })}
                        <span className="mx-1">→</span>
                        {fmtINR(h.cur, { compact: true })}
                        {h.cagr != null && (
                          <span className="ml-1">· {h.cagr.toFixed(1)}% CAGR</span>
                        )}
                      </div>
                    </div>
                    <div className="text-right shrink-0">
                      <div
                        className={`num-mono text-sm font-bold ${up ? "text-emerald-600" : "text-rose-600"}`}
                      >
                        {up ? "+" : ""}
                        {h.retPct.toFixed(1)}%
                      </div>
                      <div
                        className={`text-[11px] num-mono ${up ? "text-emerald-600/70" : "text-rose-600/70"}`}
                      >
                        {up ? "+" : ""}
                        {fmtINR(h.gain, { compact: true })}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {best && (
              <p className="mt-3 text-[11px] text-slate-400">
                Ranked by return since purchase.{" "}
                {best.name} is your best ({best.retPct >= 0 ? "+" : ""}
                {best.retPct.toFixed(1)}%). A 0% simply means the current value
                equals what you invested — update it to see real returns.
                Interest/income assets (MIS, FD) show their returns in the
                cashflow card, not here.
              </p>
            )}
          </>
        )}
      </Card>

      {/* Cashflow + Liquidity */}
      <section className="grid lg:grid-cols-2 gap-6">
        <Card
          icon={Banknote}
          title="Monthly cashflow"
          desc="What you earn vs what you pay each month."
        >
          {monthlyIn + monthlyOut === 0 ? (
            <Empty>No monthly income, SIPs or EMIs recorded yet.</Empty>
          ) : (
            <>
              {/* IN vs OUT headline */}
              <div className="grid grid-cols-2 gap-3">
                <div className="rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200/60 dark:border-emerald-500/20 p-4">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-emerald-700/80 dark:text-emerald-300/80">
                    <span className="h-2 w-2 rounded-full bg-emerald-500" /> Money
                    in
                  </div>
                  <div className="mt-1 text-2xl font-bold text-emerald-600">
                    {fmtINR(monthlyIn, { compact: true })}
                    <span className="text-sm font-medium text-emerald-600/60">
                      /mo
                    </span>
                  </div>
                </div>
                <div className="rounded-2xl bg-rose-50 dark:bg-rose-500/10 border border-rose-200/60 dark:border-rose-500/20 p-4">
                  <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-rose-700/80 dark:text-rose-300/80">
                    <span className="h-2 w-2 rounded-full bg-rose-500" /> Money
                    out
                  </div>
                  <div className="mt-1 text-2xl font-bold text-rose-600">
                    {fmtINR(monthlyOut, { compact: true })}
                    <span className="text-sm font-medium text-rose-600/60">
                      /mo
                    </span>
                  </div>
                </div>
              </div>

              {/* Net */}
              <div
                className={`mt-3 flex items-center justify-between rounded-xl px-3.5 py-3 ${
                  netCashflow >= 0
                    ? "bg-emerald-50 dark:bg-emerald-500/10"
                    : "bg-rose-50 dark:bg-rose-500/10"
                }`}
              >
                <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                  Net monthly cashflow
                </span>
                <span
                  className={`num-mono text-lg font-bold ${
                    netCashflow >= 0 ? "text-emerald-600" : "text-rose-600"
                  }`}
                >
                  {netCashflow >= 0 ? "+" : "−"}
                  {fmtINR(Math.abs(netCashflow), { compact: true })}/mo
                </span>
              </div>

              {/* Magnitude comparison */}
              <div className="mt-4 space-y-2.5">
                <FlowMagnitude
                  label="In"
                  amount={monthlyIn}
                  max={Math.max(monthlyIn, monthlyOut, 1)}
                  color="bg-emerald-500"
                />
                <FlowMagnitude
                  label="Out"
                  amount={monthlyOut}
                  max={Math.max(monthlyIn, monthlyOut, 1)}
                  color="bg-rose-500"
                />
              </div>

              {/* Breakdowns */}
              <div className="mt-5 grid sm:grid-cols-2 gap-5">
                <FlowBreakdown
                  title="Coming in"
                  rows={inflowRows}
                  total={monthlyIn}
                  barColor="bg-emerald-500"
                  dot="bg-emerald-500"
                />
                <FlowBreakdown
                  title="Going out"
                  rows={outflowRows}
                  total={monthlyOut}
                  barColor="bg-rose-500"
                  dot="bg-rose-500"
                  showTag
                />
              </div>
            </>
          )}
        </Card>

        <Card
          icon={Droplets}
          title="Liquidity ladder"
          desc="How quickly you could reach your money in an emergency."
        >
          {ladderTotal === 0 ? (
            <Empty>No assets to assess.</Empty>
          ) : (
            <>
              <div className="flex h-4 rounded-full overflow-hidden bg-slate-100 dark:bg-slate-800">
                <div
                  className="bg-emerald-500"
                  style={{ width: `${(ladder.immediate / ladderTotal) * 100}%` }}
                  title="Immediate"
                />
                <div
                  className="bg-sky-500"
                  style={{ width: `${(ladder.short / ladderTotal) * 100}%` }}
                  title="Short term"
                />
                <div
                  className="bg-slate-400 dark:bg-slate-600"
                  style={{ width: `${(ladder.locked / ladderTotal) * 100}%` }}
                  title="Locked"
                />
              </div>
              <div className="grid grid-cols-3 gap-3 mt-4">
                <LadderStat
                  color="bg-emerald-500"
                  label="Immediate"
                  value={ladder.immediate}
                  total={ladderTotal}
                />
                <LadderStat
                  color="bg-sky-500"
                  label="Short term"
                  value={ladder.short}
                  total={ladderTotal}
                />
                <LadderStat
                  color="bg-slate-400"
                  label="Locked"
                  value={ladder.locked}
                  total={ladderTotal}
                />
              </div>
              <Takeaway
                tone={
                  (ladder.immediate + ladder.short) / ladderTotal >= 0.3
                    ? "good"
                    : "warn"
                }
              >
                You could raise{" "}
                <b>{fmtINR(ladder.immediate + ladder.short, { compact: true })}</b>{" "}
                fairly quickly, while{" "}
                {fmtINR(ladder.locked, { compact: true })} (
                {Math.round((ladder.locked / ladderTotal) * 100)}%) is locked in
                long-term holdings.
              </Takeaway>
            </>
          )}
        </Card>
      </section>

      {/* Debt payoff */}
      <Card
        icon={TrendingDown}
        title="Debt payoff & interest cost"
        desc="What your loans really cost and when they disappear."
      >
        {loans.length === 0 ? (
          <Empty>You have no outstanding debt. 🎉</Empty>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat
                label="Outstanding"
                value={fmtINR(totOutstanding, { compact: true })}
                accent="text-rose-600"
              />
              <Stat
                label="Interest left"
                value={anyInterest ? fmtINR(totInterest, { compact: true }) : "—"}
                accent="text-amber-600"
              />
              <Stat label="Avg rate" value={`${wRate.toFixed(1)}%`} />
              <Stat label="Debt-free by" value={debtFreeYear || "—"} accent="text-emerald-600" />
            </div>

            {debtSeries.length > 1 && (
              <div className="h-56 mt-5">
                <ResponsiveContainer>
                  <AreaChart data={debtSeries}>
                    <defs>
                      <linearGradient id="dbt" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="#ef4444" stopOpacity={0.3} />
                        <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,0.2)" />
                    <XAxis dataKey="year" tick={{ fontSize: 11 }} />
                    <YAxis
                      tickFormatter={(v) => fmtINR(v, { compact: true })}
                      tick={{ fontSize: 11 }}
                      width={56}
                    />
                    <Tooltip
                      formatter={(v) => fmtINR(v)}
                      labelFormatter={(l) => `${l}`}
                      contentStyle={{ fontSize: 12, borderRadius: 8 }}
                    />
                    <Area
                      type="monotone"
                      dataKey="balance"
                      stroke="#ef4444"
                      strokeWidth={2.5}
                      fill="url(#dbt)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            )}

            <div className="mt-4 rounded-2xl border border-slate-200 dark:border-slate-700 overflow-hidden">
              {loans.map((l) => (
                <div
                  key={l.id}
                  className="flex items-center justify-between gap-3 px-4 py-2.5 border-t border-slate-100 dark:border-slate-800 first:border-t-0"
                >
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 dark:text-slate-100 truncate">
                      {l.name}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {titleize(l.category)}
                      {num(l.interest_rate) ? ` · ${l.interest_rate}%` : ""}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="num-mono text-sm font-semibold">
                      {fmtINR(num(l.outstanding), { compact: true })}
                    </div>
                    <div className="text-[11px] text-slate-400">
                      {l.am.months
                        ? `clears ${new Date().getFullYear() + Math.ceil(l.am.months / 12)}`
                        : "set EMI to project"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </Card>

      {/* Maturity calendar */}
      <Card
        icon={CalendarClock}
        title="Upcoming maturities & key dates"
        desc="Money that comes due — so nothing slips through the cracks."
      >
        {events.length === 0 ? (
          <Empty>No upcoming maturities, loan end-dates or goal targets.</Empty>
        ) : (
          <div className="relative pl-6">
            <div className="absolute left-[7px] top-1 bottom-1 w-px bg-slate-200 dark:bg-slate-700" />
            <div className="space-y-4">
              {events.slice(0, 12).map((e, i) => {
                const yrs = yearsBetween(now, e.date);
                const dot =
                  e.tone === "blue"
                    ? "bg-blue-500"
                    : e.tone === "violet"
                      ? "bg-violet-500"
                      : "bg-emerald-500";
                return (
                  <div key={i} className="relative">
                    <span
                      className={`absolute -left-6 top-1 h-3.5 w-3.5 rounded-full ring-4 ring-white dark:ring-slate-900 ${dot}`}
                    />
                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-slate-900 dark:text-white truncate">
                          {e.title}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {e.kind} · {fmtDate(e.date)}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="num-mono text-sm font-semibold">
                          {fmtINR(e.amount, { compact: true })}
                        </div>
                        <div className="text-[11px] text-slate-400">
                          {yrs < 1 ? "< 1 yr" : `${Math.round(yrs)} yrs`}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------
// bits
// ---------------------------------------------------------------------------
function Kpi({ icon: Icon, label, value, sub, accent = "text-slate-900 dark:text-white" }) {
  return (
    <div className="card-soft p-5">
      <div className="flex items-center justify-between">
        <div className="grid h-9 w-9 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
          <Icon className="h-4 w-4" />
        </div>
        <span className="text-[10px] text-slate-500 uppercase tracking-wider text-right">
          {sub}
        </span>
      </div>
      <div className="mt-4 text-xs text-slate-500 uppercase tracking-wide">{label}</div>
      <div className={`mt-1 num-mono text-2xl font-bold ${accent}`}>{value}</div>
    </div>
  );
}

function Card({ icon: Icon, title, desc, children }) {
  return (
    <div className="card-soft p-6">
      <div className="flex items-start gap-3 mb-5">
        <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600">
          <Icon className="h-5 w-5" />
        </div>
        <div>
          <h3 className="font-display text-lg font-bold text-slate-900 dark:text-white">
            {title}
          </h3>
          {desc && <p className="text-xs text-slate-500 mt-0.5">{desc}</p>}
        </div>
      </div>
      {children}
    </div>
  );
}

function Stat({ label, value, sub, accent = "text-slate-900 dark:text-white" }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`num-mono text-lg font-bold mt-1 ${accent}`}>{value}</div>
      {sub && <div className="text-[11px] text-slate-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function FlowMagnitude({ label, amount, max, color }) {
  return (
    <div className="flex items-center gap-3">
      <span className="w-8 shrink-0 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
        {label}
      </span>
      <div className="flex-1 h-2.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
        <div
          className={`h-full rounded-full ${color}`}
          style={{ width: `${Math.max(4, (amount / max) * 100)}%` }}
        />
      </div>
      <span className="num-mono text-xs font-semibold text-slate-600 dark:text-slate-300 w-16 text-right shrink-0">
        {fmtINR(amount, { compact: true })}
      </span>
    </div>
  );
}

function FlowBreakdown({ title, rows, total, barColor, dot, showTag }) {
  return (
    <div>
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wider text-slate-400 mb-2.5">
        <span className={`h-2 w-2 rounded-full ${dot}`} /> {title}
      </div>
      {!rows || rows.length === 0 ? (
        <p className="text-xs text-slate-400">Nothing here yet.</p>
      ) : (
        <div className="space-y-2">
          {rows.slice(0, 6).map((r, i) => (
            <div key={`${r.name}-${i}`}>
              <div className="flex justify-between items-center gap-2 text-xs mb-0.5">
                <span className="flex items-center gap-1.5 min-w-0 text-slate-600 dark:text-slate-300">
                  {showTag && r.kind && (
                    <span className="shrink-0 rounded px-1.5 py-0.5 text-[9px] font-bold uppercase bg-slate-100 dark:bg-slate-800 text-slate-500">
                      {r.kind}
                    </span>
                  )}
                  <span className="truncate">{r.name}</span>
                </span>
                <span className="num-mono text-slate-500 shrink-0">
                  {fmtINR(r.amt, { compact: true })}
                </span>
              </div>
              <div className="h-1.5 rounded-full bg-slate-100 dark:bg-slate-800 overflow-hidden">
                <div
                  className={`h-full ${barColor}`}
                  style={{ width: `${total > 0 ? (r.amt / total) * 100 : 0}%` }}
                />
              </div>
            </div>
          ))}
          {rows.length > 6 && (
            <div className="text-[11px] text-slate-400 pt-0.5">
              +{rows.length - 6} more
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function LadderStat({ color, label, value, total }) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-700 p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-slate-500">
        <span className={`h-2 w-2 rounded-full ${color}`} /> {label}
      </div>
      <div className="num-mono text-base font-bold mt-1 text-slate-900 dark:text-white">
        {fmtINR(value, { compact: true })}
      </div>
      <div className="text-[11px] text-slate-400">
        {total > 0 ? Math.round((value / total) * 100) : 0}%
      </div>
    </div>
  );
}

function Takeaway({ tone = "info", children }) {
  const tones = {
    good: "bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-200/60 dark:border-emerald-500/20",
    warn: "bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300 border-amber-200/60 dark:border-amber-500/20",
    info: "bg-slate-50 dark:bg-slate-800/50 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700",
  };
  return (
    <div className={`mt-4 rounded-xl border p-3 text-xs leading-relaxed ${tones[tone]}`}>
      {children}
    </div>
  );
}

function Empty({ children }) {
  return <div className="py-6 text-center text-sm text-slate-500">{children}</div>;
}

function Skeleton() {
  return (
    <div className="space-y-6 animate-pulse">
      <div className="grid sm:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="h-28 rounded-2xl bg-slate-100 dark:bg-slate-800" />
        ))}
      </div>
      <div className="h-80 rounded-2xl bg-slate-100 dark:bg-slate-800" />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="h-64 rounded-2xl bg-slate-100 dark:bg-slate-800" />
        <div className="h-64 rounded-2xl bg-slate-100 dark:bg-slate-800" />
      </div>
    </div>
  );
}
